import React, { useState, useEffect, useMemo, Fragment } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-modal';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { HotKeys } from 'react-hotkeys';
import { useMediaQuery } from 'react-responsive';
import debounce from 'lodash.debounce';
import qs from 'query-string';

import { IMAGE_SIZE_CODE } from '../../../../constants/image-size-codes';
import Spinner from '../../../global/Spinner';
import ArrowLeft from '../../../icons/ArrowLeft';
import DismissButton from '../../../global/DismissButton';
import ProgressivePhoto from '../../ProgressivePhoto';
import ImagePreload from '../../../global/ImagePreload';
import { getThumbBySize } from '../../../../util/photo';
import EyeSlashIcon from '../../../icons/EyeSlashIcon';
import { ALBUM_PHOTO_STATUS } from '../../../../constants/albums';
import { breakpointDown } from '../../../../services/responsive/responsive';
import DotTrigger from '../../../global/DotTrigger';
import DropdownMenu from '../../../global/DropdownMenu';

export const LOAD_MORE_THRESHOLD = 5;

const propTypes = {
  recordId: PropTypes.string.isRequired,
  records: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
    }),
  ),
  loadMore: PropTypes.func,
  shouldLoadMore: PropTypes.bool,
  recordPhotoAccessor: PropTypes.string,
  renderPhoto: PropTypes.func,
  actions: PropTypes.func,
  footer: PropTypes.func,
  imageSizeCode: PropTypes.oneOf(Object.values(IMAGE_SIZE_CODE)),
  gridPath: PropTypes.string,
  forceActionsDropdown: PropTypes.bool,
};

const PhotoGridViewer = ({
  records,
  recordId,
  shouldLoadMore,
  loadMore,
  recordPhotoAccessor,
  renderPhoto,
  imageSizeCode = IMAGE_SIZE_CODE.LARGE,
  actions,
  footer,
  gridPath,
  forceActionsDropdown,
}) => {
  const [loading, setLoading] = useState(false);
  const match = useRouteMatch();
  const history = useHistory();
  const location = useLocation();
  const { url } = match;
  const basePath = url.split('/').slice(0, -1).join('/');
  const { showForeground } = qs.parse(location.search);

  /**
   * Checks if more records are necessary to load and loads them
   * if we're approaching the end of the navigation (right-hand-wise)
   * so we would need to load more records to continue to the next record.
   *
   * @returns {Promise<void>}
   */
  const maybeLoadMoreRecords = async () => {
    if (loading || !loadMore || !shouldLoadMore) {
      return;
    }

    const currentRecordIndex = records.findIndex(({ id }) => recordId === id);

    // If we're approaching the end and there are more records, load them
    // to keep a steady progression to the next record
    if (
      records.length - currentRecordIndex <= LOAD_MORE_THRESHOLD ||
      // If we land on a photo not loaded yet, we need to query stuff until we get it
      currentRecordIndex === -1
    ) {
      setLoading(true);
      await loadMore();
      setLoading(false);
    }
  };

  useEffect(() => {
    maybeLoadMoreRecords();
  }, [recordId, records.length, loading]);

  /**
   * Gets the adjacent record ID (ie previous or next one).
   *
   * @param {number} adjacency - 1 for "next" or -1 for "previous".
   * @returns {string|undefined}
   */
  const getAdjacentRecordId = adjacency => {
    const currentRecordIndex = records.findIndex(({ id }) => id === recordId);
    const nextRecord = records[currentRecordIndex + 1];
    const prevRecord = records[currentRecordIndex - 1];

    if (currentRecordIndex === -1) {
      return undefined;
    }

    if (adjacency === 1 && nextRecord) {
      return nextRecord.id;
    }

    if (adjacency === -1 && prevRecord) {
      return prevRecord.id;
    }

    return undefined;
  };

  /**
   * Navigates through records (ie previous or next).
   *
   * @param {number} adjacency - 1 for "next" or -1 for "previous".
   */
  const onNavigateRecords = adjacency => {
    const nextRecordId = getAdjacentRecordId(adjacency);

    if (!nextRecordId) {
      return;
    }

    history.replace(`${basePath}/${nextRecordId}`);
  };

  /**
   * Dismisses the Photo Viewer
   */
  const onDismiss = () => {
    if (history.length > 1) {
      // Avoids leaving "gridPath" twice on the queue
      // {gridPath}/v/{photoId} gets added to the queue on initial photo render.
      // it needs to be removed if the user closes the photo viewer
      history.goBack();
    } else {
      history.replace(gridPath);
    }
  };

  /**
   * Helper method that navigates to either the next or prev record
   * depending on whether they exist (with that priority) or dismisses
   * the photo viewer in case neither exist.
   */
  const onNavigateNextAfterRemoval = () => {
    const nextRecordId = getAdjacentRecordId(1);

    if (!nextRecordId) {
      const prevRecordId = getAdjacentRecordId(-1);

      if (!prevRecordId) {
        onDismiss();
      } else {
        history.replace(`${basePath}/${prevRecordId}`);
      }

      return;
    }

    history.replace(`${basePath}/${nextRecordId}`);
  };

  const getPhotoFromRecord = record => {
    if (!record) {
      return undefined;
    }

    return recordPhotoAccessor ? record?.[recordPhotoAccessor] : record;
  };

  const record = records.find(({ id }) => recordId === id);
  const nextRecordId = getAdjacentRecordId(1);
  const prevRecordId = getAdjacentRecordId(-1);

  const photo = getPhotoFromRecord(record);
  const nextPhoto = getPhotoFromRecord(
    records.find(({ id }) => nextRecordId === id),
  );

  const isMobile = useMediaQuery(breakpointDown('sm'));
  const debouncedOnDismiss = debounce(onDismiss);

  const thumbnails = useMemo(() => {
    if (
      ['true', '1', 1, true].includes(showForeground) &&
      photo?.foreground?.original_url
    ) {
      return [photo?.foreground?.original_url];
    }

    return photo?.watermarked_urls || photo?.thumbnail_urls || [];
  }, [photo, showForeground]);

  const progressivePhotoKey = useMemo(() => {
    if (showForeground) {
      return `${recordId}-foreground`;
    }

    return recordId;
  }, [recordId, showForeground]);

  return (
    <Modal
      ariaHideApp={false}
      isOpen
      bodyOpenClassName="body-modal-open"
      overlayClassName="photo-grid-viewer-overlay"
      className="photo-grid-viewer"
    >
      <div className="photo-grid-viewer-header">
        <div className="photo-grid-viewer-header-left">
          <DismissButton
            className="photo-grid-viewer-btn-dismiss"
            onClick={onDismiss}
          />
        </div>

        {!!record && actions && (
          <div className="photo-grid-viewer-header-right">
            {isMobile || forceActionsDropdown ? (
              <div className="dropdown-menu-dark">
                <DropdownMenu
                  toggle={
                    <DotTrigger className="is-overlay" dotType="vertical" />
                  }
                  placement="bottom-end"
                  closeOnClickWithin
                >
                  {actions({
                    record,
                    onPrevRecord: () => onNavigateRecords(-1),
                    onNextRecord: () => onNavigateRecords(1),
                    onNavigateNextAfterRemoval,
                    nextRecordId,
                    prevRecordId,
                    isDropDown: true,
                  })}
                </DropdownMenu>
              </div>
            ) : (
              <>
                {actions({
                  record,
                  onPrevRecord: () => onNavigateRecords(-1),
                  onNextRecord: () => onNavigateRecords(1),
                  onNavigateNextAfterRemoval,
                  nextRecordId,
                  prevRecordId,
                  isDropDown: false,
                })}
              </>
            )}
          </div>
        )}
      </div>

      <div className="photo-grid-viewer-body">
        <button
          className="photo-grid-viewer-nav photo-grid-viewer-nav-prev"
          onClick={() => onNavigateRecords(-1)}
          disabled={!prevRecordId}
        >
          <span className="photo-grid-viewer-nav-outline">
            <ArrowLeft color="#fff" />
          </span>
        </button>

        <div className="photo-grid-viewer-content">
          {photo ? (
            (() => {
              if (renderPhoto) {
                return renderPhoto({ photo, record });
              }

              return (
                <Fragment>
                  <ProgressivePhoto
                    id={recordId}
                    key={progressivePhotoKey}
                    thumbnails={thumbnails}
                    size={imageSizeCode}
                    allowFallbackThumbnail
                  />
                  {record?.album_photo_status &&
                    record?.album_photo_status !==
                      ALBUM_PHOTO_STATUS.PUBLISHED && (
                      <div className="hidden-photo-overlay">
                        <EyeSlashIcon />
                      </div>
                    )}
                </Fragment>
              );
            })()
          ) : (
            <Spinner />
          )}
        </div>

        <button
          className="photo-grid-viewer-nav photo-grid-viewer-nav-next"
          onClick={() => onNavigateRecords(1)}
          disabled={!nextRecordId}
        >
          <span className="photo-grid-viewer-nav-outline">
            <ArrowLeft color="#fff" />
          </span>
        </button>
      </div>

      <div className="photo-grid-viewer-footer">
        {!!records && footer && (
          <>
            {footer({
              record,
              onPrevRecord: () => onNavigateRecords(-1),
              onNextRecord: () => onNavigateRecords(1),
              onNavigateNextAfterRemoval,
              nextRecordId,
              prevRecordId,
            })}
          </>
        )}
      </div>

      {nextPhoto && (
        <ImagePreload
          urls={[getThumbBySize(nextPhoto.thumbnail_urls, imageSizeCode)?.url]}
        />
      )}

      {photo?.foreground?.original_url && (
        <ImagePreload urls={[photo?.foreground?.original_url]} />
      )}

      <HotKeys
        handlers={{
          // Debounce to avoid calling onDismiss twice here.
          // At least on windows, seems esc and escape are treated the same:
          esc: debouncedOnDismiss,
          escape: debouncedOnDismiss,
          right: nextRecordId ? () => onNavigateRecords(1) : undefined,
          left: prevRecordId ? () => onNavigateRecords(-1) : undefined,
        }}
        attach={window}
        focused
      />
    </Modal>
  );
};

PhotoGridViewer.propTypes = propTypes;

export default PhotoGridViewer;
